import { StorageUploadFile } from '../storage/common';
import { StorageAdapter, StorageCallback } from '../storage/StorageAdapter';
import { ConsoleLogger, ILogger, NoOpLogger } from '../utils/logger';

/**
 * Abstract Base Service Class for Firebase Storage with Logging
 * All storage services MUST extend this class and implement required methods
 * 
 * Handles:
 * - File uploads (with automatic path generation)
 * - File downloads (signed URLs)
 * - File deletion
 * - File metadata
 * - Automatic logging
 */
abstract class StorageService {
    protected logger: ILogger;

    constructor(
        protected adapter: StorageAdapter,
        logger?: ILogger | boolean
    ) {
        // Use provided logger or default to NoOpLogger (does nothing)
        this.logger = !logger ?
            new NoOpLogger()
            : ((logger === true) || this.shouldLog()) ?
                new ConsoleLogger()
                : logger;
    }

    /**
     * Get the base storage path for this service
     * Example: 'chat-media', 'listing-images', 'user-avatars'
     */
    protected abstract getBasePath(): string;

    /**
     * Get the service name for logging
     */
    protected getServiceName(): string {
        return this.constructor.name;
    }

    /**
     * Enable/disable logging
     */
    protected shouldLog(): boolean {
        return true;
    }

    /**
     * Generate a unique filename
     * Override to customize naming strategy
     */
    protected generateFilename(originalName: string): string {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8);
        const ext = this.getFileExtension(originalName);
        return `${timestamp}_${random}${ext}`;
    }

    /**
     * Get file extension from filename
     */
    protected getFileExtension(filename: string): string {
        const lastDot = filename.lastIndexOf('.');
        return lastDot !== -1 ? filename.substring(lastDot) : '';
    }

    /**
     * Get MIME type from filename
     */
    protected getMimeType(filename: string): string {
        const ext = this.getFileExtension(filename).toLowerCase();
        const mimeTypes: Record<string, string> = {
            // Images
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.png': 'image/png',
            '.gif': 'image/gif',
            '.webp': 'image/webp',
            '.svg': 'image/svg+xml',

            // Videos
            '.mp4': 'video/mp4',
            '.mov': 'video/quicktime',
            '.avi': 'video/x-msvideo',
            '.webm': 'video/webm',

            // Audio
            '.mp3': 'audio/mpeg',
            '.wav': 'audio/wav',
            '.ogg': 'audio/ogg',

            // Documents
            '.pdf': 'application/pdf',
            '.doc': 'application/msword',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            '.xls': 'application/vnd.ms-excel',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.txt': 'text/plain',
            '.md': 'text/markdown',

            // Archives
            '.zip': 'application/zip',
            '.rar': 'application/x-rar-compressed',
        };

        return mimeTypes[ext] || 'application/octet-stream';
    }

    /**
     * Validate file size
     * Override to customize size limits
     */
    protected getMaxFileSize(): number {
        return 10 * 1024 * 1024; // 10MB default
    }

    /**
     * Validate file type
     * Override to customize allowed types
     */
    protected getAllowedMimeTypes(): string[] {
        // Allow all by default
        return [];
    }

    /**
     * Validate file before upload
     */
    protected validateFile(buffer: Buffer, mimeType: string): void {
        // Check size
        const maxSize = this.getMaxFileSize();
        if (buffer.length > maxSize) {
            throw new Error(
                `File size ${buffer.length} bytes exceeds maximum ${maxSize} bytes`
            );
        }

        // Check type
        const allowedTypes = this.getAllowedMimeTypes();
        if (allowedTypes.length > 0 && !allowedTypes.includes(mimeType)) {
            throw new Error(
                `File type ${mimeType} not allowed. Allowed types: ${allowedTypes.join(', ')}`
            );
        }
    }

    // ============================================
    // LOGGING HELPERS
    // ============================================

    protected logOperation(operation: string, path: string, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation}`;
        this.logger.debug(message, { path, basePath: this.getBasePath(), data });
    }

    protected logSuccess(operation: string, path: string, result?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} succeeded`;
        this.logger.log(message, { path, basePath: this.getBasePath(), result });
    }

    protected logError(operation: string, path: string, error: Error, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} failed`;
        this.logger.error(message, {
            path,
            basePath: this.getBasePath(),
            error: error.message,
            stack: error.stack,
            data,
        });
    }

    // ============================================
    // ABSTRACT METHODS - MUST BE IMPLEMENTED
    // ============================================

    /**
     * Upload a file
     * @param buffer - File buffer
     * @param filename - Original filename
     * @param metadata - Optional metadata
     * @returns Object with file path and download URL
     */
    abstract upload(
        buffer: Buffer,
        filename: string,
        metadata?: Record<string, any>
    ): Promise<{ path: string; url: string; size: number }>;

    /**
     * Delete a file
     * @param path - File path in storage
     */
    abstract delete(path: string): Promise<void>;

    /**
     * Get download URL for a file
     * @param path - File path in storage
     * @param expiresIn - URL expiration in milliseconds (default: 7 days)
     */
    abstract getDownloadUrl(path: string, expiresIn?: number): Promise<string>;

    // ============================================
    // OPTIONAL METHODS - CAN BE OVERRIDDEN
    // ============================================

    /**
     * Check if file exists
     */
    async exists(path: string): Promise<boolean> {
        this.logOperation('exists', path);

        try {
            const exists = await this.adapter.exists(path);

            this.logSuccess('exists', path, { exists });
            return exists;
        } catch (error) {
            this.logError('exists', path, error as Error);
            throw error;
        }
    }

    /**
     * Get file metadata
     */
    async getMetadata(path: string): Promise<Record<string, any>> {
        this.logOperation('getMetadata', path);

        try {

            const metadata = await this.adapter.getMetadata(path);

            this.logSuccess('getMetadata', path, { size: metadata.size });
            return metadata;
        } catch (error) {
            this.logError('getMetadata', path, error as Error);
            throw error;
        }
    }

    /**
     * Update file metadata
     */
    async updateMetadata(
        path: string,
        metadata: Record<string, any>
    ): Promise<void> {
        this.logOperation('updateMetadata', path, metadata);

        try {
            await this.adapter.updateMetadata(path, { metadata });

            this.logSuccess('updateMetadata', path);
        } catch (error) {
            this.logError('updateMetadata', path, error as Error, metadata);
            throw error;
        }
    }

    /**
     * Copy file to new location
     */
    async copy(sourcePath: string, destinationPath: string): Promise<void> {
        this.logOperation('copy', sourcePath, { sourcePath, destinationPath });

        try {
            await this.adapter.copy(sourcePath, destinationPath);

            this.logSuccess('copy', sourcePath, { sourcePath, destinationPath });
        } catch (error) {
            this.logError('copy', sourcePath, error as Error, { sourcePath, destinationPath });
            throw error;
        }
    }

    /**
     * Move file to new location
     */
    async move(sourcePath: string, destinationPath: string): Promise<void> {
        this.logOperation('move', sourcePath, { destinationPath });

        try {
            this.adapter.move(sourcePath, destinationPath);

            this.logSuccess('move', sourcePath, { destinationPath });
        } catch (error) {
            this.logError('move', sourcePath, error as Error, { destinationPath });
            throw error;
        }
    }

    /**
     * Download file as buffer
     */
    async download(path: string) {
        this.logOperation('download', path);

        try {
            const buffer = await this.adapter.download(path);

            this.logSuccess('download', path, { size: buffer.length });
            return buffer;
        } catch (error) {
            this.logError('download', path, error as Error);
            throw error;
        }
    }

    /**
     * List files in a directory
     */
    async listFiles(prefix?: string) {
        const searchPrefix = prefix || this.getBasePath();
        this.logOperation('listFiles', searchPrefix);

        try {
            const files = await this.adapter.listFiles(searchPrefix);

            this.logSuccess('listFiles', searchPrefix, { count: files.length });
            return files;
        } catch (error) {
            this.logError('listFiles', searchPrefix, error as Error);
            throw error;
        }
    }

    /**
     * Delete all files in a directory
     */
    async deleteDirectory(prefix: string): Promise<void> {
        this.logOperation('deleteDirectory', prefix);

        try {
            const files = await this.listFiles(prefix);

            await Promise.all(files.map(file => this.delete(file.path)));

            this.logSuccess('deleteDirectory', prefix, { count: files.length });
        } catch (error) {
            this.logError('deleteDirectory', prefix, error as Error);
            throw error;
        }
    }

    /**
     * Make file public
     */
    async makePublic(path: string): Promise<string> {
        this.logOperation('makePublic', path);

        try {
            const publicUrl = await this.adapter.makePublic(path);

            this.logSuccess('makePublic', path, { url: publicUrl });
            return publicUrl;
        } catch (error) {
            this.logError('makePublic', path, error as Error);
            throw error;
        }
    }

    /**
     * Make file private
     */
    async makePrivate(path: string): Promise<void> {
        this.logOperation('makePrivate', path);

        try {
            await this.adapter.makePrivate(path)

            this.logSuccess('makePrivate', path);
        } catch (error) {
            this.logError('makePrivate', path, error as Error);
            throw error;
        }
    }

    /**
     * Get file size
     */
    async getFileSize(path: string): Promise<number> {
        this.logOperation('getFileSize', path);

        try {
            const metadata = await this.getMetadata(path);
            const size = parseInt(metadata.size);

            this.logSuccess('getFileSize', path, { size });
            return size;
        } catch (error) {
            this.logError('getFileSize', path, error as Error);
            throw error;
        }
    }

    /**
     * Batch delete files
     */
    async batchDelete(paths: string[]): Promise<void> {
        this.logOperation('batchDelete', 'batch', { count: paths.length, paths });

        try {
            await Promise.all(paths.map(path => this.delete(path)));

            this.logSuccess('batchDelete', 'batch', { count: paths.length });
        } catch (error) {
            this.logError('batchDelete', 'batch', error as Error, { count: paths.length });
            throw error;
        }
    }
}

/**
 * Base Storage Service Implementation with Logging
 * Provides default implementations for most use cases
 * 
 * This eliminates code duplication across storage services.
 */
export abstract class BaseStorageService extends StorageService {

    /**
     * Upload a file to storage
     * Automatically generates unique filename and returns download URL
     */
    async upload(
        buffer: StorageUploadFile,
        filename: string,
        callback?: StorageCallback,
        metadata?: Record<string, any>
    ): Promise<{ path: string; url: string; size: number, filename: string }> {
        const mimeType = this.getMimeType(filename);
        const generatedFilename = this.generateFilename(filename);
        const path = `${this.getBasePath()}/${generatedFilename}`;

        this.logOperation('upload', path, {
            originalFilename: filename,
            size: buffer.length || 0,
            mimeType,
        });

        try {
            // Validate file
            this.validateFile(buffer, mimeType);

            //upload through the adapter
            await this.adapter.upload(path, buffer, callback, metadata);

            // Get download URL (7 days expiration by default)
            const url = await this.getDownloadUrl(path);

            this.logSuccess('upload', path, {
                url,
                size: buffer.length || 0,
                filename: generatedFilename,
            });

            return { path, url, size: buffer.length || 0, filename: generatedFilename };
        } catch (error) {
            this.logError('upload', path, error as Error, {
                filename,
                size: buffer.length
            });
            throw error;
        }
    }

    /**
     * Delete a file from storage
     */
    async delete(path: string): Promise<void> {
        this.logOperation('delete', path);

        try {
            await this.adapter.delete(path);

            this.logSuccess('delete', path);
        } catch (error) {
            this.logError('delete', path, error as Error);
            throw error;
        }
    }

    /**
     * Get download URL for a file
     * @param path The path of the file to get the url from
     * @param expiresIn if provided URL expires after specified time (default: 7 days) or true for default
     */
    async getDownloadUrl(path: string, expiresIn?: number | true): Promise<string> {
        this.logOperation('getDownloadUrl', path, { expiresIn });

        try {
            const url = await this.adapter.getDownloadUrl(path, expiresIn)

            this.logSuccess('getDownloadUrl', path, { url });
            return url;
        } catch (error) {
            this.logError('getDownloadUrl', path, error as Error, { expiresIn });
            throw error;
        }
    }
}

/**
 * Base Storage Service Implementation for content in Markdown
 * Provides default implementations for most use cases
 * 
 * This eliminates code duplication across storage services.
 */
export abstract class BaseMarkdownStorageService extends BaseStorageService {

    // Customize file size limit (20MB for news media)
    protected getMaxFileSize(): number {
        return 20 * 1024 * 1024; // 20MB
    }

    // Only allow markdown file
    protected getAllowedMimeTypes(): string[] {
        return ['text/markdown'];
    }

    protected generateFilename(originalName: string): string {
        return originalName;//keep as is
    }

    /** Upload Markdown content */
    async uploadMarkdown(
        contentMD: string,
        language_code?: string,
        callback?: StorageCallback,
        metadata?: Record<string, any>
    ): Promise<{ path: string; url: string; size: number }> {
        const fileContent = new Blob([contentMD], { type: 'text/markdown' })
        return this.upload(
            fileContent,
            `${language_code || "en"}.md`, //default to english if not provided
            callback,
            metadata);
    }

    /** Get Markdown content from file */
    async getMarkdown(
        language_code?: string,
    ) {
        //helper function for the path of the file
        const DEFAULT_LANGUAGE = "en";
        const buildFilePath = (locale?: string) => this.getBasePath() + `/${locale || DEFAULT_LANGUAGE}.md`;

        const language: string = language_code || DEFAULT_LANGUAGE;//defaulting to English

        let path = buildFilePath(language);
        let exist = await this.adapter.exists(path);
        if (!exist && language.includes("-")) {
            //if the file doesn't exist and language have specifics (ex: es-MX), try with the basic language
            const basicLanguage = language.split("-")[0];
            path = buildFilePath(basicLanguage)
            this.logOperation(
                'getMDContent',
                path,
                `File does not exist with language ${language}, trying with basic language ${basicLanguage}.`,
            )
            exist = await this.adapter.exists(path);
        }
        if (!exist) {
            //if the file doesn't exist, try with the default language
            path = buildFilePath(DEFAULT_LANGUAGE)
            this.logOperation(
                'getMDContent',
                path,
                `File does not exist with language ${language}, trying with default language ${DEFAULT_LANGUAGE}.`,
            );
            exist = await this.adapter.exists(path);
        }
        if (!exist) {
            //if the file is still not found, throw an error
            const message = `Could not find File with language ${language} (neither with default language ${DEFAULT_LANGUAGE})`
            this.logError(
                'getMDContent',
                path,
                new Error(message),
            );
            throw new Error(message);
        }
        const download = await this.adapter.download(path);
        const string = download.toString();
        this.logSuccess(
            'getMDContent',
            path,
            string);
        return string;
    }

    /**
     * Custom method to delete a specific md folder.
     * Warning: will delete all md and its related locales
     */
    async deleteMarkdown(): Promise<void> {
        return this.deleteDirectory(this.getBasePath());
    }
}